import { useState } from 'react';
import { Drawer, Box, Typography, List, ListItem, ListItemButton, IconButton, Chip, Divider, Snackbar, Alert, Tooltip } from '@mui/material';
import { CyberSpinner } from './CyberSpinner';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MapIcon from '@mui/icons-material/Map';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import GoogleIcon from '@mui/icons-material/Google';
// Apple Maps and Waze removed - only Google Maps supports waypoints
// MUI Icons for facility types - matching quick search buttons
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WaterIcon from '@mui/icons-material/Water';
import ConstructionIcon from '@mui/icons-material/Construction';
import BoltIcon from '@mui/icons-material/Bolt';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import SettingsIcon from '@mui/icons-material/Settings';
import FactoryIcon from '@mui/icons-material/Factory';
import OpacityIcon from '@mui/icons-material/Opacity';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import ScienceIcon from '@mui/icons-material/Science';
import AdjustIcon from '@mui/icons-material/Adjust';
import HubIcon from '@mui/icons-material/Hub';
import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import PropaneTankIcon from '@mui/icons-material/PropaneTank';
import LandscapeIcon from '@mui/icons-material/Landscape';
import SpeedIcon from '@mui/icons-material/Speed';
import AirIcon from '@mui/icons-material/Air';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import RecyclingIcon from '@mui/icons-material/Recycling';
import HealingIcon from '@mui/icons-material/Healing';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import EngineeringIcon from '@mui/icons-material/Engineering';
import VerifiedIcon from '@mui/icons-material/Verified';
import BusinessIcon from '@mui/icons-material/Business';
import { Well } from '../types';
import { useTranslation } from '../i18n/translations';
import { telegram } from '@services/telegram';
import { getCyberColors } from '../theme/cyberColors';
import { useAppStore } from '../stores/appStore';

interface FavoritesPanelProps {
  open: boolean;
  favorites: Well[];
  loading: boolean;
  onClose: () => void;
  onSelectWell: (well: Well) => void;
  onRemoveFavorite: (well: Well) => void;
  onViewOnMap?: (well: Well) => void;
  onAddNew?: () => void; // Opens search to add new favorite
}

// MUI Icon mapping for facility types - matches quick search buttons exactly
const FACILITY_MUI_ICONS: Record<string, { icon: React.ComponentType<any>; color: string }> = {
  // Main types
  well: { icon: LocalGasStationIcon, color: '#f59e0b' },
  wells: { icon: LocalGasStationIcon, color: '#f59e0b' },
  swd: { icon: WaterIcon, color: '#10b981' },
  swds: { icon: WaterIcon, color: '#10b981' },
  rig: { icon: ConstructionIcon, color: '#fbbf24' },
  rigs: { icon: ConstructionIcon, color: '#fbbf24' },
  // Tank & Storage
  tb: { icon: BoltIcon, color: '#06b6d4' },
  tankbattery: { icon: BoltIcon, color: '#06b6d4' },
  gs: { icon: PropaneTankIcon, color: '#fbbf24' },
  gasstorage: { icon: PropaneTankIcon, color: '#fbbf24' },
  bgt: { icon: LandscapeIcon, color: '#78716c' },
  belowgradetank: { icon: LandscapeIcon, color: '#78716c' },
  // Texas well type facets
  'tx-horizontal': { icon: TrendingFlatIcon, color: '#06b6d4' },
  'tx-directional': { icon: NorthEastIcon, color: '#a855f7' },
  'tx-oil': { icon: OilBarrelIcon, color: '#f59e0b' },
  'tx-gas': { icon: LocalFireDepartmentIcon, color: '#ef4444' },
  'tx-oil-gas': { icon: LocalGasStationIcon, color: '#fbbf24' },
  // Flare & Emissions
  fs: { icon: WhatshotIcon, color: '#ef4444' },
  flarestack: { icon: WhatshotIcon, color: '#ef4444' },
  // Compressor & Pumps
  cs: { icon: SettingsIcon, color: '#60a5fa' },
  compressor: { icon: SettingsIcon, color: '#60a5fa' },
  cp: { icon: AirIcon, color: '#64748b' },
  crudepump: { icon: AirIcon, color: '#64748b' },
  // Plants & Processing
  gp: { icon: FactoryIcon, color: '#a855f7' },
  gasplant: { icon: FactoryIcon, color: '#a855f7' },
  rf: { icon: OilBarrelIcon, color: '#a855f7' },
  refinery: { icon: OilBarrelIcon, color: '#a855f7' },
  tp: { icon: ScienceIcon, color: '#f472b6' },
  treatingplant: { icon: ScienceIcon, color: '#f472b6' },
  inj: { icon: MedicationLiquidIcon, color: '#34d399' },
  injectionplant: { icon: MedicationLiquidIcon, color: '#34d399' },
  // Pipelines
  plg: { icon: LinearScaleIcon, color: '#3b82f6' },
  gaspipeline: { icon: LinearScaleIcon, color: '#3b82f6' },
  plc: { icon: LinearScaleIcon, color: '#64748b' },
  crudepipeline: { icon: LinearScaleIcon, color: '#64748b' },
  plw: { icon: LinearScaleIcon, color: '#0ea5e9' },
  waterpipeline: { icon: LinearScaleIcon, color: '#0ea5e9' },
  // Gathering & Metering
  ggs: { icon: HubIcon, color: '#3b82f6' },
  gasgathering: { icon: HubIcon, color: '#3b82f6' },
  mgs: { icon: SpeedIcon, color: '#8b5cf6' },
  meteringstation: { icon: SpeedIcon, color: '#8b5cf6' },
  wh: { icon: AdjustIcon, color: '#f59e0b' },
  wellhead: { icon: AdjustIcon, color: '#f59e0b' },
  wellpad: { icon: AdjustIcon, color: '#f59e0b' },
  // Brine Well
  bw: { icon: OpacityIcon, color: '#0ea5e9' },
  brinewell: { icon: OpacityIcon, color: '#0ea5e9' },
  // Waste & Environmental
  pit: { icon: LandscapeIcon, color: '#78716c' },
  pits: { icon: LandscapeIcon, color: '#78716c' },
  wm: { icon: DeleteIcon, color: '#ef4444' },
  wastemgmt: { icon: DeleteIcon, color: '#ef4444' },
  rfl: { icon: RecyclingIcon, color: '#22c55e' },
  recycling: { icon: RecyclingIcon, color: '#22c55e' },
  rm: { icon: HealingIcon, color: '#eab308' },
  remediate: { icon: HealingIcon, color: '#eab308' },
  ab: { icon: CleaningServicesIcon, color: '#f97316' },
  abatement: { icon: CleaningServicesIcon, color: '#f97316' },
  // Service & Regulatory
  sc: { icon: EngineeringIcon, color: '#64748b' },
  serviceco: { icon: EngineeringIcon, color: '#64748b' },
  ci: { icon: VerifiedIcon, color: '#34d399' },
  classiii: { icon: VerifiedIcon, color: '#34d399' },
  // Generic
  facility: { icon: BusinessIcon, color: '#06b6d4' },
  default: { icon: BusinessIcon, color: '#06b6d4' }
};

// Component to render facility icon using MUI icons
function FacilityIcon({ type, facilityType, size = 32 }: { type: string; facilityType?: string; size?: number }) {
  let iconType = (type || 'well').toLowerCase();
  
  // For facilities with specific subtypes like "Brine Well - (BW)", extract the code
  if (type === 'facility' && facilityType) {
    const match = facilityType.match(/\(([A-Z0-9]+)\)/);
    if (match) {
      iconType = match[1].toLowerCase();
    } else {
      iconType = facilityType.toLowerCase().replace(/[\s_-]/g, '');
    }
  }
  
  const iconConfig = FACILITY_MUI_ICONS[iconType] || FACILITY_MUI_ICONS.default;
  const IconComponent = iconConfig.icon;
  
  return (
    <Box
      sx={{
        width: size + 8,
        height: size + 8,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${iconConfig.color}20`,
        border: `2px solid ${iconConfig.color}`,
        flexShrink: 0
      }}
    >
      <IconComponent sx={{ fontSize: size * 0.65, color: iconConfig.color }} />
    </Box>
  );
}

export function FavoritesPanel({ open, favorites, loading, onClose, onSelectWell, onRemoveFavorite, onViewOnMap, onAddNew }: FavoritesPanelProps) {
  const [toast, setToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const { t } = useTranslation();
  const { darkMode, highContrast } = useAppStore();
  const cyber = getCyberColors(darkMode, highContrast);

  // Open Google Maps with driving directions
  const handleGoogleMaps = (well: Well, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    telegram.hapticFeedback('light');
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${well.latitude},${well.longitude}&travelmode=driving`, '_blank');
  };

  // Apple Maps and Waze removed - only Google Maps supports waypoints

  // In-app navigation
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '92%', sm: 420 },
          background: darkMode 
            ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          borderLeft: `1px solid ${cyber.borderDefault}`,
        }
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ color: '#fbbf24', fontSize: '1.75rem' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: cyber.textPrimary }}>
              {t('favorites')}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => { telegram.hapticFeedback('light'); onClose(); }}
            sx={{ 
              width: 48,
              height: 48,
              color: cyber.textSecondary,
              '&:hover': { color: cyber.textPrimary }
            }}
          >
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: cyber.borderDefault, mb: 2 }} />

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CyberSpinner size={40} />
          </Box>
        )}

        {/* Empty State */}
        {!loading && favorites.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <StarIcon sx={{ fontSize: '4rem', color: 'rgba(251, 191, 36, 0.3)', mb: 2 }} />
            <Typography variant="body1" sx={{ color: cyber.textSecondary, mb: 1 }}>
              {t('noFavorites')}
            </Typography>
            <Typography variant="body2" sx={{ color: cyber.textMuted, mb: 3 }}>
              {t('tapToSave')}
            </Typography>
            {onAddNew && (
              <IconButton
                onClick={() => {
                  telegram.hapticFeedback('light');
                  onAddNew();
                }}
                sx={{
                  width: 60,
                  height: 60,
                  backgroundColor: cyber.neonBlue,
                  border: `3px solid ${cyber.neonCyan}`,
                  borderRadius: '50%',
                  boxShadow: `0 0 20px ${cyber.neonBlue}40`,
                  '&:hover': {
                    backgroundColor: cyber.neonCyan,
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <AddIcon sx={{ fontSize: 32, color: '#fff' }} />
              </IconButton>
            )}
          </Box>
        )}

        {/* Favorites List */}
        {!loading && favorites.length > 0 && (
          <>
            <Typography variant="body2" sx={{ color: cyber.textSecondary, mb: 2 }}>
              {favorites.length} {favorites.length === 1 ? t('savedFacility') : t('savedFacilities')}
            </Typography>

            <List sx={{ p: 0 }}>
              {favorites.map((well, index) => (
                <ListItem 
                  key={well.id || index}
                  disablePadding
                  sx={{ mb: 1 }}
                >
                  <ListItemButton
                    onClick={() => onSelectWell(well)}
                    sx={{
                      borderRadius: '12px',
                      backgroundColor: darkMode ? '#1e293b' : '#ffffff',
                      border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                      boxShadow: '0 6px 16px rgba(96, 165, 250, 0.2)',
                      p: 2,
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      transform: 'scale(1)',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: '0 12px 28px rgba(96, 165, 250, 0.35)',
                      }
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <FacilityIcon 
                          type={(well as any).type || 'well'} 
                          facilityType={(well as any).facilityType}
                          size={36}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: cyber.textPrimary,
                            flex: 1,
                            fontSize: '1.1rem',
                            lineHeight: 1.25
                          }}
                        >
                          {well.wellName}
                        </Typography>
                      </Box>

                      {well.api && (
                        <Chip
                          label={well.api}
                          size="small"
                          icon={<ContentCopyIcon sx={{ fontSize: '0.8rem !important' }} />}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            navigator.clipboard.writeText(well.api).then(() => {
                              telegram.hapticFeedback('success');
                              setToast({ open: true, message: t('apiCopied', well.api) });
                            });
                          }}
                          sx={{
                            mb: 0.5,
                            height: 26,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                            backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.15)' : 'rgba(30, 64, 175, 0.1)',
                            color: cyber.chipBlue,
                            cursor: 'pointer',
                            '& .MuiChip-icon': { color: cyber.chipBlue },
                            '&:hover': {
                              backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                            }
                          }}  
                        />
                      )}

                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                        {well.latitude && well.longitude && (
                          <Chip
                            label={`${well.latitude.toFixed(4)}, ${well.longitude.toFixed(4)}`}
                            size="small"
                            icon={<LocationOnIcon sx={{ fontSize: '0.8rem !important' }} />}
                            onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${well.latitude},${well.longitude}`;
                              window.open(mapsUrl, '_blank');
                            }}
                            sx={{
                              height: '24px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                              backgroundColor: darkMode ? 'rgba(251, 191, 36, 0.15)' : 'rgba(180, 83, 9, 0.1)',
                              color: darkMode ? '#fbbf24' : '#b45309',
                              cursor: 'pointer',
                              '& .MuiChip-icon': { color: darkMode ? '#fbbf24' : '#b45309' },
                              '&:hover': { backgroundColor: darkMode ? 'rgba(251, 191, 36, 0.3)' : 'rgba(180, 83, 9, 0.2)' }
                            }}
                          />
                        )}
                        <Chip
                          label={well.operator}
                          size="small"
                          sx={{
                            height: '24px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                            backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.15)' : 'rgba(30, 64, 175, 0.08)',
                            color: cyber.chipBlue,
                            border: 'none'
                          }}
                        />
                        <Chip
                          label={well.county}
                          size="small"
                          sx={{
                            height: '24px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                            backgroundColor: darkMode ? 'rgba(52, 211, 153, 0.15)' : 'rgba(6, 95, 70, 0.1)',
                            color: cyber.chipGreen,
                            border: 'none'
                          }}
                        />
                      </Box>

                      {/* Action Buttons Row - Single line at bottom */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 0.5, mt: 'auto', pt: 0.5 }}>
                        {/* View on Map */}
                        <Tooltip title={t('viewOnMapTooltip')} arrow>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              telegram.hapticFeedback('light');
                              onClose();
                              if (onViewOnMap) {
                                onViewOnMap(well);
                              } else {
                                onSelectWell(well);
                              }
                            }}
                            sx={{
                              width: 40,
                              height: 40,
                              color: cyber.neonBlue,
                              backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.12)' : 'rgba(30, 64, 175, 0.1)',
                              '&:hover': {
                                backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.25)' : 'rgba(30, 64, 175, 0.2)',
                              }
                            }}
                          >
                            <MapIcon sx={{ fontSize: '1.25rem' }} />
                          </IconButton>
                        </Tooltip>

                        {/* Google Maps */}
                        <Tooltip title={t('googleTooltip')} arrow>
                          <IconButton
                            onClick={(e) => handleGoogleMaps(well, e)}
                            sx={{
                              width: 40,
                              height: 40,
                              color: '#4285F4',
                              backgroundColor: darkMode ? 'rgba(66, 133, 244, 0.15)' : 'rgba(66, 133, 244, 0.1)',
                              '&:hover': {
                                backgroundColor: darkMode ? 'rgba(66, 133, 244, 0.3)' : 'rgba(66, 133, 244, 0.2)',
                              }
                            }}
                          >
                            <GoogleIcon sx={{ fontSize: '1.25rem' }} />
                          </IconButton>
                        </Tooltip>

                        {/* Delete */}
                        <Tooltip title={t('remove')} arrow>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveFavorite(well);
                            }}
                            sx={{
                              width: 40,
                              height: 40,
                              color: 'rgba(239, 68, 68, 0.85)',
                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              '&:hover': {
                                color: '#ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.2)'
                              }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: '1.25rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Box>

      {/* Toast for copy feedback */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2000}
        onClose={() => setToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '60px' }}
      >
        <Alert severity="success" sx={{ backgroundColor: 'rgba(34, 197, 94, 0.9)', color: 'white' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Drawer>
  );
}
